import { IImage, IVideo } from 'shared/models/media';
import { IUserAuthInfo } from 'shared/models/user-auth-info';
import { EUserType, UserTypeUtil } from 'shared/models/common';

export enum ECommentPermission {
  All = 0, // 显示评论区
  Follower = 1, // 粉丝可评论
  Self = 2, // 自己可评论
  Disable = 3, // 不可评论，不显示评论区
  Friend = 4, // 好友可评论
}

export interface GalleryLabelType {
  direction: string;
  id: string;
  imageIndex: number;
  name: string;
  type: string;
  xPct: number;
  yPct: number;
}

export enum EArticleClass {
  GALLERY = 'Gallery',
  VIDEO = 'Video',
  ARTICLE = 'Article',
  FIZZ = 'Fizz',
}

export enum EWarningLabelType {
  DANGER = 101,
  DANGER_PROFESSIONAL = 102,
  COSMETIC_SURGERY = 103,
  SHOCKING = 401,
  ELECTION = 10001,
}

export interface IIllegalInfo {
  warningLabel: string;
  warningLabelType: EWarningLabelType;
}

export interface IArticleDetail {
  groupId: string;
  templateId?: string;
  imprId?: string;

  // author
  author: {
    userId: string;
    avatar: string;
    authInfo: IUserAuthInfo;
    nickName: string;
    linkName: string;
    userType: EUserType;
  };

  // content
  articleClass: EArticleClass;

  title: string;
  content: string;
  shortContent: string;

  // content publish time
  publishTime: number;
  modifyTime?: number;

  // interaction
  readCount: number;
  diggCount: number;
  favoriteCount: number;
  commentCount: number;

  // legacy hashtag
  forumList?: {
    id: string;
    name: string;
  }[];

  // poi
  locationInfo?: {
    poiId: string;
    poiName: string;
    poiArea?: string;
  };

  // search after read
  relatedQuery?: string;

  // privacy & safety
  isOnlyFollow: boolean;
  commentPermission: ECommentPermission;
  illegalInfo?: IIllegalInfo;
}

export interface IVideoArticleDetail extends IArticleDetail {
  articleClass: EArticleClass.VIDEO;
  largeImage: IImage;
  video: IVideo;
}

export interface IGalleryArticleDetail extends IArticleDetail {
  articleClass: EArticleClass.GALLERY;
  imageList: IImage[];
  galleryLabels: GalleryLabelType[];
}

export interface IFizzArticleDetail extends IArticleDetail {
  articleClass: EArticleClass.FIZZ;
  textCoverList: IImage[];
}

export interface IRichArticleDetail extends IArticleDetail {
  articleClass: EArticleClass.ARTICLE;
  largeImage: IImage;
}

export type TArticleDetail = IVideoArticleDetail | IGalleryArticleDetail | IRichArticleDetail | IFizzArticleDetail;

export enum EArticleUnavailableReason {
  NOT_FOUND = 'not_found',
  PRIVATE_ACCOUNT = 'private_account',
  SERVER_ERROR = 'server_error',
}

export interface IArticleUnavailableDetail {
  groupId: string;
  unavailableReason: EArticleUnavailableReason;
}

export const ArticleDetailUtil = {
  isAvailable: (article: TArticleDetail | IArticleUnavailableDetail): article is TArticleDetail => {
    return !('unavailableReason' in article);
  },
  getCoverImage: (article: TArticleDetail): IImage => {
    switch (article.articleClass) {
      case EArticleClass.GALLERY:
        return article.imageList[0];
      case EArticleClass.FIZZ:
        return article.textCoverList[0];
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return article.largeImage;
    }
  },
  getArticlePath: (article: TArticleDetail): string => {
    const prefix = UserTypeUtil.getLinkNamePrefix(article.author.userType);

    return `/${prefix}${article.author.linkName}/${article.groupId}`;
  },
  getAuthorPath: (article: TArticleDetail): string => {
    const prefix = UserTypeUtil.getLinkNamePrefix(article.author.userType);

    return `/${prefix}${article.author.linkName}`;
  },
};
